if (!customElements.get('upsell-variant-picker')) {
  customElements.define(
    'upsell-variant-picker',
    class UpsellVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.variants = [];
      }

      connectedCallback() {
        this.variantSelects = this.querySelector('variant-selects');
        this.variantIdInput = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.addToCartText = this.dataset.addToCartText || 'Add to cart';
        this.soldOutText = this.dataset.soldOutText || 'Sold out';

        if (!this.variantIdInput || !this.submitButton) return;

        try {
          this.variants = JSON.parse(this.dataset.productVariants || '[]');
        } catch (error) {
          console.error('Failed to parse upsell variants JSON', error);
          this.variants = [];
        }

        if (!this.variants.length) return;

        this.syncInputsToActiveVariant();
        this.selectedOptions = this.getCurrentSelectedOptions();
        this.variantSelects?.addEventListener('click', (event) => {
          const label = event.target.closest('label[for]');
          if (!label || !this.variantSelects.contains(label)) return;

          const inputId = label.getAttribute('for');
          if (!inputId) return;

          const escapedInputId =
            typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(inputId) : inputId;
          const input = this.variantSelects.querySelector(`#${escapedInputId}`);
          if (!input || input.disabled || input.checked) return;

          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this.variantSelects?.addEventListener('change', () => {
          this.selectedOptions = this.getCurrentSelectedOptions();
          this.updateOptionAvailability();
          this.updateVariantState();
        });

        this.updateOptionAvailability();
        this.updateVariantState();
      }

      syncInputsToActiveVariant() {
        if (!this.variantSelects) return;

        const activeVariant = this.getActiveVariant();
        if (!activeVariant) return;

        const fieldsets = Array.from(this.variantSelects.querySelectorAll('fieldset'));
        if (fieldsets.length) {
          fieldsets.forEach((fieldset, optionIndex) => {
            const targetValue = this.getVariantOptionValue(activeVariant, optionIndex);
            if (targetValue == null) return;

            const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
            const matchingRadio = radios.find((radio) => radio.value === targetValue);
            if (matchingRadio) matchingRadio.checked = true;
          });
          return;
        }

        const selects = Array.from(this.variantSelects.querySelectorAll('select'));
        selects.forEach((select, optionIndex) => {
          const targetValue = this.getVariantOptionValue(activeVariant, optionIndex);
          if (targetValue == null) return;

          const matchingOption = Array.from(select.options).find((option) => option.value === targetValue);
          if (!matchingOption) return;

          select.value = targetValue;
          Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
          matchingOption.setAttribute('selected', 'selected');
        });
      }

      getCurrentSelectedOptions() {
        const activeVariant = this.getActiveVariant();
        if (!this.variantSelects) {
          return activeVariant?.options ? [...activeVariant.options] : [];
        }

        const fieldsets = Array.from(this.variantSelects.querySelectorAll('fieldset'));
        if (fieldsets.length) {
          return fieldsets
            .map((fieldset, optionIndex) => {
              const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
              return checkedInput?.value ?? this.getVariantOptionValue(activeVariant, optionIndex);
            })
            .filter((value) => value != null && value !== '');
        }

        const selects = Array.from(this.variantSelects.querySelectorAll('select'));
        if (selects.length) {
          return selects
            .map((select, optionIndex) => {
              const selectedOption = select.options[select.selectedIndex] || select.querySelector('option[selected]');
              return selectedOption?.value ?? this.getVariantOptionValue(activeVariant, optionIndex);
            })
            .filter((value) => value != null && value !== '');
        }

        return activeVariant?.options ? [...activeVariant.options] : [];
      }

      findMatchingVariant() {
        if (!this.variants.length) return null;

        const activeVariant = this.getActiveVariant();
        const optionCount = this.variants[0]?.options?.length || 0;
        const selectedOptions = this.selectedOptions || [];
        const resolvedOptions = Array.from({ length: optionCount }, (_, optionIndex) => {
          return selectedOptions[optionIndex] ?? this.getVariantOptionValue(activeVariant, optionIndex);
        });

        if (resolvedOptions.length) {
          const bySelectedOptions = this.variants.find(
            (variant) =>
              resolvedOptions.length === (variant.options?.length || 0) &&
              resolvedOptions.every(
                (selectedValue, optionIndex) => this.getVariantOptionValue(variant, optionIndex) === selectedValue
              )
          );
          if (bySelectedOptions) return bySelectedOptions;
        }

        return activeVariant;
      }

      getVariantOptionValue(variant, optionIndex) {
        return variant.options?.[optionIndex] ?? variant[`option${optionIndex + 1}`];
      }

      getActiveVariant() {
        const activeVariantId = parseInt(this.variantIdInput.value, 10);
        const byCurrentVariantId = this.variants.find((variant) => variant.id === activeVariantId);
        if (byCurrentVariantId) return byCurrentVariantId;

        const firstAvailableVariant = this.variants.find((variant) => variant.available);
        if (firstAvailableVariant) return firstAvailableVariant;

        return this.variants[0] || null;
      }

      isValueAvailableForSelection(optionIndex, optionValue) {
        const selectedOptions = this.selectedOptions || [];
        return this.variants.some((variant) => {
          if (!variant.available) return false;
          if (this.getVariantOptionValue(variant, optionIndex) !== optionValue) return false;

          return selectedOptions.every((selectedValue, selectedIndex) => {
            if (selectedIndex === optionIndex || !selectedValue) return true;
            return this.getVariantOptionValue(variant, selectedIndex) === selectedValue;
          });
        });
      }

      updateOptionAvailability() {
        if (!this.variantSelects) return;

        const fieldsets = Array.from(this.variantSelects.querySelectorAll('fieldset'));
        fieldsets.forEach((fieldset, optionIndex) => {
          const optionInputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
          optionInputs.forEach((input) => {
            const isAvailable = this.isValueAvailableForSelection(optionIndex, input.value);
            const isSwatchInput = input.classList.contains('swatch-input__input');

            if (isSwatchInput) {
              input.classList.toggle('visually-disabled', !isAvailable);
              input.classList.remove('disabled');
            } else {
              input.classList.toggle('disabled', !isAvailable);
              input.classList.remove('visually-disabled');
            }
          });
        });
      }

      updateVariantState() {
        const matchingVariant = this.findMatchingVariant();

        const isAvailable = !!matchingVariant?.available;
        this.variantIdInput.value = matchingVariant?.id || '';
        this.variantIdInput.disabled = !isAvailable;
        this.submitButton.disabled = !isAvailable;

        if (this.submitButtonText) {
          this.submitButtonText.textContent = isAvailable ? this.addToCartText : this.soldOutText;
        }
      }
    }
  );
}
